<template>
<div
  class="z-tree"
  :class="{
    'has-header': !!$slots.header
  }"
  @dragover.prevent
  @drop="ondrop({srcData: {id: null}})"
>
  <div
    v-if="$slots.header"
    class="z-tree-header"
  >
    <slot name="header" />
  </div>

  <div class="z-tree-list-wrapper">
    <ul class="z-tree-list" style="padding:3px 2px;">
      <z-tree-node
        v-for="node in tree"
        :key="node.id"
        :data="node"
        :id-prop="idProp"
        :name-prop="nameProp"
        :parent-id-prop="parentIdProp"
        :show-checkbox="Array.isArray(value)"
        :deep="0"
        :selected-id="value"
        :default-expanded="expandAll"
        :draggable="draggable"
        @node-click="onNodeClick"
        @node-dblclick="onNodeDblclick"
        @dragstart="ondragstart"
        @drop="ondrop"
      >
        <template v-if="$scopedSlots.node" slot-scope="{node}">
          <slot name="node" :node="node" />
        </template>
      </z-tree-node>
    </ul>
  </div>
</div>
</template>

<script>
import ZTreeNode from './ZTreeNode'

export default {
  name: 'ZTree',
  components: {ZTreeNode},
  props: {
    value: {},
    list: {
      required: true,
      type: Array
    },
    idProp: {
      type: String,
      default: 'id'
    },
    nameProp: {
      type: String,
      default: 'name'
    },
    parentIdProp: {
      type: String,
      default: 'parentId'
    },
    expandAll: {
      type: String
    },
    draggable: {
      type: Boolean
    },
    leafSelectOnly: {
      type: Boolean
    }
  },
  computed: {
    treeList () {
      return this.list.map(item => ({
        ...JSON.parse(JSON.stringify(item)),
        srcData: item
      }))
    },
    treeMap () {
      let {idProp} = this
      return this.treeList.reduce((map, item) => {
        map[item[idProp]] = item
        return map
      }, {})
    },
    tree () {
      let {parentIdProp} = this
      let map = this.treeMap

      return this.treeList.reduce((roots, item) => {
        let parentId = item[parentIdProp]
        let parent = map[parentId]

        if (parent) {
          let {children} = parent

          if (!children) {
            children = parent.children = []
          }

          children.push(item)
        } else {
          roots.push(item)
        }

        return roots
      }, [])
    }
  },
  methods: {
    onNodeClick (node) {
      if (this.leafSelectOnly && node.children) {
        return
      }

      let id = node[this.idProp]

      if (id === this.value) {
        return
      }

      this.$emit('input', id)
      this.$emit('change', node)
    },
    onNodeDblclick (node) {
      if (this.leafSelectOnly && node.children) {
        return
      }

      let id = node[this.idProp]

      if (id !== this.value) {
        this.$emit('input', id)
        this.$emit('change', node)
      }

      this.$emit('dblclick', node)
    },
    ondragstart (node) {
      this.dragNode = node
    },
    ondrop (dropNode) {
      let {dragNode} = this
      let {srcData} = dragNode
      let targetData = dropNode.srcData

      if (srcData === targetData || srcData.parentId === targetData.id) {
        return
      }

      let {children} = dragNode

      if (children) {
        let tmp = [...children]

        for (let i = 0; i < tmp.length; i++) {
          let node = tmp[i]

          if (node === dropNode) {
            return
          }

          let {children} = node

          if (children) {
            tmp.push(...children)
          }
        }
      }

      this.$emit('node-dragend', srcData, targetData)

      delete this.dragNode
    }
  }
}
</script>

<style lang="scss">
.z-tree {
  --common-padding: .5em .8em;
  --z-tree-border-var: var(--z-tree-border, 1px solid #EBEEF5);

  display: grid;
  grid-template-rows: auto;
  height: 100%;
  border: var(--z-tree-border-var);

  &.has-header {
    grid-template-rows: max-content auto;

    > .z-tree-header {
      display: grid;
      grid-template-columns: auto max-content;
      align-items: center;
      min-height: 32px;
      padding: var(--common-padding);
      border-bottom: var(--z-tree-border-var);
      background: var(--z-tree-header-bg, #f9f9f9);
    }
  }

  > * {
    min: {
      width: 0;
      height: 0;
    }
  }

  > .z-tree-body {
    overflow: auto;
  }
}

.z-tree-header {
  > * {
    margin: 0
  }
}

.z-tree-list-wrapper {
  overflow: auto;

  .z-tree-list {
    margin: 0;
    padding: 0;
    list-style: none;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/ZTree/index.vue